import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  X, Send, Paperclip, Image, Video, Trash2, Eye, EyeOff,
  Clock, Download, AlertTriangle, ChevronDown, ChevronUp, Maximize2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RepairNotesPanel = ({ deviceRef, onClose, isClient = false }) => {
  const { api } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newText, setNewText] = useState('');
  const [visibility, setVisibility] = useState('public');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState({});
  const [expandedNote, setExpandedNote] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef(null);
  const newNoteFileRef = useRef(null);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);

  useEffect(() => {
    fetchNotes();
  }, [deviceRef]);

  const fetchNotes = async () => {
    try {
      const res = await api.get(`/repair-notes/${deviceRef}`);
      setNotes(res.data || []);
    } catch (err) {
      console.error('Error fetching notes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendNote = async () => {
    if (!newText.trim() && !pendingFile) return;
    setSending(true);
    try {
      const res = await api.post('/repair-notes', {
        device_ref: deviceRef,
        text: newText.trim() || (pendingFile ? `Fichier: ${pendingFile.name}` : ''),
        visibility
      });
      const noteId = res.data?.id;

      // Upload pending file if any
      if (pendingFile && noteId) {
        const formData = new FormData();
        formData.append('file', pendingFile);
        try {
          await api.post(`/repair-notes/${noteId}/attachments`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch (err) {
          alert(err.response?.data?.detail || 'Erreur upload fichier');
        }
      }

      setNewText('');
      setPendingFile(null);
      if (newNoteFileRef.current) newNoteFileRef.current.value = '';
      fetchNotes();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de l\'ajout');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Supprimer cette note et ses pièces jointes ?')) return;
    try {
      await api.delete(`/repair-notes/${noteId}`);
      fetchNotes();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur');
    }
  };

  const triggerUpload = (noteId) => {
    setUploadTarget(noteId);
    fileRef.current?.click();
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadTarget) return;

    const isVid = file.type.startsWith('video/');
    const maxSize = isVid ? 100 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(`Fichier trop volumineux. Max: ${isVid ? '100 MB' : '5 MB'}`);
      e.target.value = '';
      return;
    }

    setUploading(prev => ({ ...prev, [uploadTarget]: true }));
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await api.post(`/repair-notes/${uploadTarget}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.warning) alert(res.data.warning);
      else if (res.data.info) alert(res.data.info);
      fetchNotes();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur d\'upload');
    } finally {
      setUploading(prev => ({ ...prev, [uploadTarget]: false }));
      setUploadTarget(null);
      e.target.value = '';
    }
  };

  const getAttachmentUrl = (noteId, fileId) => {
    const token = localStorage.getItem('token');
    return `${API_URL}/api/repair-notes/${noteId}/attachments/${fileId}?token=${token}`;
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const formatSize = (bytes) => {
    if (bytes > 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / 1024).toFixed(0)} KB`;
  };

  return (
    <div className="flex flex-col h-full max-h-[80vh]" data-testid="repair-notes-panel">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <h3 className="font-bold text-lg text-cyan-400" data-testid="notes-panel-title">
          Notes - {deviceRef}
        </h3>
        {onClose && (
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="notes-panel-close">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Notes list */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="notes-list">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="loading-spinner"></div>
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center py-8 text-gray-500" data-testid="notes-empty">
            Aucune note pour cette réparation
          </div>
        ) : (
          notes.map((note) => (
            <div
              key={note.id}
              className={`rounded-xl border p-3 transition-all ${
                note.visibility === 'internal'
                  ? 'bg-amber-500/5 border-amber-500/20'
                  : 'bg-white/5 border-white/10'
              }`}
              data-testid={`note-item-${note.id}`}
            >
              {/* Note header */}
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-white">{note.author}</span>
                  <span className="text-xs text-gray-500">{formatDate(note.created_at)}</span>
                  {note.visibility === 'internal' ? (
                    <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30" data-testid={`note-badge-internal-${note.id}`}>
                      <EyeOff className="w-3 h-3" /> Interne
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 border border-green-500/30" data-testid={`note-badge-public-${note.id}`}>
                      <Eye className="w-3 h-3" /> Public
                    </span>
                  )}
                </div>
                {!isClient && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => triggerUpload(note.id)}
                      className="p-1.5 rounded-lg hover:bg-white/10 text-gray-400 hover:text-cyan-400 transition-colors"
                      title="Joindre un fichier"
                      disabled={uploading[note.id]}
                      data-testid={`note-attach-btn-${note.id}`}
                    >
                      {uploading[note.id] ? (
                        <div className="loading-spinner w-4 h-4"></div>
                      ) : (
                        <Paperclip className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => handleDelete(note.id)}
                      className="p-1.5 rounded-lg hover:bg-red-500/20 text-gray-400 hover:text-red-400 transition-colors"
                      title="Supprimer"
                      data-testid={`note-delete-btn-${note.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Note text */}
              <p className="text-sm text-gray-300 whitespace-pre-wrap" data-testid={`note-text-${note.id}`}>
                {note.text}
              </p>

              {/* Attachments */}
              {note.attachments?.length > 0 && (
                <div className="mt-3 space-y-2">
                  <button
                    onClick={() => setExpandedNote(expandedNote === note.id ? null : note.id)}
                    className="flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300"
                    data-testid={`note-toggle-attachments-${note.id}`}
                  >
                    <Paperclip className="w-3 h-3" />
                    {note.attachments.length} pièce{note.attachments.length > 1 ? 's' : ''} jointe{note.attachments.length > 1 ? 's' : ''}
                    {expandedNote === note.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  </button>

                  {expandedNote === note.id && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2" data-testid={`note-attachments-${note.id}`}>
                      {note.attachments.map((att) => (
                        <div key={att.id} className="rounded-lg border border-white/10 bg-black/30 overflow-hidden group">
                          {att.is_photo ? (
                            <div className="relative group">
                              <img
                                src={getAttachmentUrl(note.id, att.id)}
                                alt={att.filename}
                                className="w-full h-24 object-cover"
                                loading="lazy"
                              />
                              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                <button
                                  onClick={() => setLightbox({ url: getAttachmentUrl(note.id, att.id), filename: att.filename })}
                                  className="p-1.5 rounded-full bg-white/20 hover:bg-white/40 text-white"
                                  title="Visionner"
                                >
                                  <Maximize2 className="w-3.5 h-3.5" />
                                </button>
                                <a
                                  href={getAttachmentUrl(note.id, att.id)}
                                  download={att.filename}
                                  className="p-1.5 rounded-full bg-white/20 hover:bg-white/40 text-white"
                                  title="Télécharger"
                                >
                                  <Download className="w-3.5 h-3.5" />
                                </a>
                              </div>
                            </div>
                          ) : att.is_video ? (
                            <video
                              src={getAttachmentUrl(note.id, att.id)}
                              className="w-full h-24 object-cover"
                              controls
                              preload="metadata"
                              data-testid={`attachment-video-${att.id}`}
                            />
                          ) : (
                            <div className="h-24 flex items-center justify-center">
                              <Download className="w-6 h-6 text-gray-500" />
                            </div>
                          )}
                          <div className="p-1.5">
                            <p className="text-[10px] text-gray-400 truncate">{att.filename}</p>
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] text-gray-600">{formatSize(att.size)}</span>
                              {att.expires_at && (
                                <span className="flex items-center gap-0.5 text-[10px] text-amber-400" title={`Expire le ${formatDate(att.expires_at)}`}>
                                  <Clock className="w-2.5 h-2.5" />
                                  {new Date(att.expires_at).toLocaleDateString('fr-FR')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* New note form (staff only) */}
      {!isClient && (
        <div className="border-t border-white/10 p-4 space-y-3" data-testid="notes-form">
          {/* Visibility toggle */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setVisibility('public')}
              className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border transition-all ${
                visibility === 'public'
                  ? 'bg-green-500/20 border-green-500/40 text-green-400'
                  : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'
              }`}
              data-testid="notes-visibility-public"
            >
              <Eye className="w-3 h-3" /> Public
            </button>
            <button
              onClick={() => setVisibility('internal')}
              className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border transition-all ${
                visibility === 'internal'
                  ? 'bg-amber-500/20 border-amber-500/40 text-amber-400'
                  : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'
              }`}
              data-testid="notes-visibility-internal"
            >
              <EyeOff className="w-3 h-3" /> Interne
            </button>
            <span className="text-[10px] text-gray-500 ml-1">
              {visibility === 'public' ? 'Visible par le client' : 'Visible uniquement par le staff'}
            </span>
          </div>

          {/* Text input + file + send */}
          <div className="space-y-2">
            {pendingFile && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-xs text-cyan-300">
                <Paperclip className="w-3 h-3" />
                <span className="truncate flex-1">{pendingFile.name}</span>
                <button onClick={() => { setPendingFile(null); if (newNoteFileRef.current) newNoteFileRef.current.value = ''; }}
                  className="text-red-400 hover:text-red-300" data-testid="notes-remove-file">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            <div className="flex gap-2">
              <textarea
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                placeholder="Ajouter une note..."
                className="input-dark flex-1 py-2 px-3 text-sm resize-none"
                rows={2}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendNote();
                  }
                }}
                data-testid="notes-text-input"
              />
              <div className="flex flex-col gap-1 self-end">
                <button
                  onClick={() => newNoteFileRef.current?.click()}
                  className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-gray-400 hover:text-cyan-400 transition-colors"
                  title="Joindre un fichier"
                  data-testid="notes-attach-file-btn"
                >
                  <Paperclip className="w-5 h-5" />
                </button>
                <button
                  onClick={handleSendNote}
                  disabled={(!newText.trim() && !pendingFile) || sending}
                  className="p-2 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-bold disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid="notes-send-btn"
                >
                  {sending ? (
                    <div className="loading-spinner w-5 h-5"></div>
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hidden file input for existing notes */}
      <input
        type="file"
        ref={fileRef}
        className="hidden"
        accept="image/*,video/*"
        onChange={handleFileUpload}
      />
      {/* Hidden file input for new note */}
      <input
        type="file"
        ref={newNoteFileRef}
        className="hidden"
        accept="image/*,video/*"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            const isVid = file.type.startsWith('video/');
            const maxSize = isVid ? 100 * 1024 * 1024 : 5 * 1024 * 1024;
            if (file.size > maxSize) {
              alert(`Fichier trop volumineux. Max: ${isVid ? '100 MB' : '5 MB'}`);
              e.target.value = '';
              return;
            }
            setPendingFile(file);
          }
        }}
      />

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <div className="relative max-w-[90vw] max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <img src={lightbox.url} alt={lightbox.filename} className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl" />
            <div className="absolute top-2 right-2 flex gap-2">
              <a href={lightbox.url} download={lightbox.filename} className="p-2 rounded-full bg-black/60 hover:bg-black/80 text-white"><Download className="w-5 h-5" /></a>
              <button onClick={() => setLightbox(null)} className="p-2 rounded-full bg-black/60 hover:bg-black/80 text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="absolute bottom-2 left-2 right-2 text-center text-sm text-white/70 bg-black/40 rounded-lg py-1">{lightbox.filename}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RepairNotesPanel;
